// Renderiza markdown (negritas, tablas, listas, código, enlaces) con estilos MUI.
// Usado por las respuestas del Asistente APS para organizar el contenido.
import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Box, Typography, Link, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Paper, Divider,
} from '@mui/material';

const components = {
  p: ({ node, ...props }) => (
    <Typography variant="body2" sx={{ my: 0.5, lineHeight: 1.6, wordBreak: 'break-word' }} {...props} />
  ),
  strong: ({ node, ...props }) => <Box component="strong" sx={{ fontWeight: 800 }} {...props} />,
  em: ({ node, ...props }) => <Box component="em" sx={{ fontStyle: 'italic' }} {...props} />,
  a: ({ node, ...props }) => <Link target="_blank" rel="noopener" underline="hover" color="primary" {...props} />,
  h1: ({ node, ...props }) => <Typography variant="subtitle1" fontWeight={800} sx={{ mt: 1, mb: 0.5 }} {...props} />,
  h2: ({ node, ...props }) => <Typography variant="subtitle1" fontWeight={800} sx={{ mt: 1, mb: 0.5 }} {...props} />,
  h3: ({ node, ...props }) => <Typography variant="subtitle2" fontWeight={800} sx={{ mt: 1, mb: 0.5 }} {...props} />,
  ul: ({ node, ...props }) => <Box component="ul" sx={{ pl: 2.5, my: 0.5 }} {...props} />,
  ol: ({ node, ...props }) => <Box component="ol" sx={{ pl: 2.5, my: 0.5 }} {...props} />,
  li: ({ node, ...props }) => (
    <Typography component="li" variant="body2" sx={{ my: 0.25, lineHeight: 1.55 }} {...props} />
  ),
  blockquote: ({ node, ...props }) => (
    <Box component="blockquote" sx={{ borderLeft: '3px solid', borderColor: 'primary.main', pl: 1.5, my: 1, color: 'text.secondary', fontStyle: 'italic' }} {...props} />
  ),
  hr: () => <Divider sx={{ my: 1 }} />,
  code: ({ node, inline, className, children, ...props }) => (
    inline ? (
      <Box component="code" sx={{ px: 0.6, py: 0.1, borderRadius: 0.8, bgcolor: 'rgba(0,0,0,0.06)', fontFamily: 'monospace', fontSize: '0.85em' }} {...props}>
        {children}
      </Box>
    ) : (
      <Box component="pre" sx={{ p: 1.25, my: 1, borderRadius: 1, bgcolor: '#1a1a1a', color: '#f1f1f1', overflowX: 'auto', fontSize: '0.82em', lineHeight: 1.5 }}>
        <Box component="code" sx={{ fontFamily: 'monospace' }} {...props}>{children}</Box>
      </Box>
    )
  ),
  table: ({ node, ...props }) => (
    <TableContainer component={Paper} variant="outlined" sx={{ my: 1, maxWidth: '100%' }}>
      <Table size="small" {...props} />
    </TableContainer>
  ),
  thead: ({ node, ...props }) => <TableHead sx={{ bgcolor: '#1a1a1a' }} {...props} />,
  tbody: ({ node, ...props }) => <TableBody {...props} />,
  tr: ({ node, ...props }) => <TableRow {...props} />,
  th: ({ node, ...props }) => (
    <TableCell sx={{ fontWeight: 800, fontSize: '0.78rem', py: 0.75, color: '#ffffff !important',
      borderBottom: 'none', whiteSpace: 'nowrap' }} {...props} />
  ),
  td: ({ node, ...props }) => <TableCell sx={{ fontSize: '0.82rem', py: 0.6, color: 'text.primary' }} {...props} />,
};

export default function Markdown({ children }) {
  return (
    <Box sx={{ '& > *:first-of-type': { mt: 0 }, '& > *:last-child': { mb: 0 } }}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {children || ''}
      </ReactMarkdown>
    </Box>
  );
}
